import type { AsyncSearchParams, PagedResult, PickFilterValues } from '../types';
import { toPagedSearchParams } from './asyncSearchParams';

export interface BuildApiParamsContext<TFilters extends PickFilterValues> {
  filters: TFilters;
  search?: AsyncSearchParams;
  ids?: number[];
}

interface PagedApiResponse<TDto> {
  items: readonly TDto[];
  totalCount: number;
}

type PagedApiHandlersBaseOptions<TItem, TDto, TApiParams, _TFilters extends PickFilterValues> = {
  fetch: (apiParams: TApiParams) => Promise<PagedApiResponse<TDto>>;
  queryKey: (apiParams: TApiParams) => readonly unknown[];
  mapItem: (dto: TDto) => TItem;
  normalizeIds?: (ids: readonly (string | number)[]) => number[];
};

export type CreatePagedApiHandlersOptions<
  TItem,
  TDto,
  TApiParams,
  TFilters extends PickFilterValues,
> = PagedApiHandlersBaseOptions<TItem, TDto, TApiParams, TFilters> &
  (
    | { buildApiParams: (ctx: BuildApiParamsContext<TFilters>) => TApiParams; mapFilters?: never }
    | { mapFilters: (filters: TFilters) => Partial<TApiParams>; buildApiParams?: never }
  );

function createStandardBuildApiParams<TApiParams, TFilters extends PickFilterValues>(
  mapFilters: (filters: TFilters) => Partial<TApiParams>
): (ctx: BuildApiParamsContext<TFilters>) => TApiParams {
  return ({ search, ids, filters }) =>
    ({
      ...(search ? toPagedSearchParams(search) : {}),
      ...(ids != null ? { ids } : {}),
      ...mapFilters(filters),
    }) as TApiParams;
}

const defaultNormalizeIds = (ids: readonly (string | number)[]) => ids.map((id) => Number(id));

export function createPagedApiHandlers<TItem, TDto, TApiParams, TFilters extends PickFilterValues>(
  options: CreatePagedApiHandlersOptions<TItem, TDto, TApiParams, TFilters>
) {
  const normalizeIds = options.normalizeIds ?? defaultNormalizeIds;
  const buildApiParams =
    options.buildApiParams ??
    createStandardBuildApiParams(options.mapFilters as (filters: TFilters) => Partial<TApiParams>);

  const search = async (
    params: AsyncSearchParams,
    filters: TFilters
  ): Promise<PagedResult<TItem>> => {
    const apiParams = buildApiParams({ search: params, filters });
    const response = await options.fetch(apiParams);
    return {
      items: response.items.map(options.mapItem),
      totalCount: response.totalCount,
    };
  };

  const queryKey = (params: AsyncSearchParams, filters: TFilters) => {
    const apiParams = buildApiParams({ search: params, filters });
    return options.queryKey(apiParams);
  };

  const resolveByIds = async (ids: readonly (string | number)[], filters: TFilters) => {
    const apiParams = buildApiParams({
      ids: normalizeIds(ids),
      filters,
    });
    const response = await options.fetch(apiParams);
    return response.items.map(options.mapItem);
  };

  return { search, queryKey, resolveByIds };
}
